import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { parseISO, format, isSameDay, differenceInCalendarDays } from "date-fns";
import clsx from "clsx";
import { alpha } from "@mui/material/styles";
import { green } from "@mui/material/colors";
import { makeStyles } from "tss-react/mui";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import Typography from "@mui/material/Typography";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

const useStyles = makeStyles()((theme) => ({
  // Row wrapper
  listItem: {
    padding: 0,
  },

  // Clickable row
  button: {
    display: "flex",
    alignItems: "flex-start",
    padding: theme.spacing(1.25, 1.5, 1.25, 1.25),
    gap: 10,
    borderLeft: "3px solid transparent",
    transition: "background-color 0.12s, border-left-color 0.12s",
    "&.Mui-selected": {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      borderLeftColor: theme.palette.primary.main,
    },
    "&.Mui-selected:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
    },
  },
  // Pending rows are not navigatable
  pendingRow: {
    cursor: "default",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },

  // Avatar
  avatar: {
    width: 40,
    height: 40,
    flexShrink: 0,
    marginTop: 1,
  },

  // Content column (right of avatar)
  content: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 3,
  },

  // Row 1: name · tags · time
  topRow: {
    display: "flex",
    alignItems: "center",
    gap: 5,
  },
  contactName: {
    flex: 1,
    minWidth: 0,
    fontWeight: 600,
    fontSize: 13.5,
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    lineHeight: "20px",
  },
  // WhatsApp connection tag
  connTag: {
    fontSize: 10,
    fontWeight: 600,
    lineHeight: "16px",
    padding: "0 6px",
    borderRadius: 8,
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
    flexShrink: 0,
    maxWidth: 72,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  // "closed" status pill
  closedPill: {
    fontSize: 10,
    fontWeight: 600,
    lineHeight: "16px",
    padding: "0 6px",
    borderRadius: 8,
    backgroundColor: theme.palette.text.disabled,
    color: theme.palette.background.paper,
    flexShrink: 0,
  },
  // Timestamp
  time: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    flexShrink: 0,
    lineHeight: "20px",
  },

  // Row 2: message preview · badge/button
  bottomRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  preview: {
    flex: 1,
    minWidth: 0,
    fontSize: 12.5,
    color: theme.palette.text.secondary,
    lineHeight: "18px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    // flatten any block-level markdown children
    "& p, & span, & em, & strong, & code, & pre": {
      display: "inline",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  // Green unread count pill
  unreadPill: {
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: green[500],
    color: "#fff",
    fontSize: 10,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 5px",
    flexShrink: 0,
    lineHeight: 1,
  },
  // Accept button (pending tickets)
  acceptBtn: {
    fontSize: 11,
    fontWeight: 700,
    padding: "2px 10px",
    borderRadius: 10,
    minHeight: 0,
    lineHeight: "18px",
    flexShrink: 0,
    cursor: "pointer",
  },
  // Row 3: label chips
  labelsRow: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    flexWrap: "wrap",
  },
  labelChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 10,
    fontWeight: 600,
    lineHeight: "15px",
    padding: "1px 6px 1px 4px",
    borderRadius: 8,
    backgroundColor: alpha(theme.palette.text.primary, 0.06),
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
}));

const TicketListItem = ({ ticket, onAccept }) => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleAcepptTicket = async (id) => {
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "open",
        userId: user?.id,
      });
      if (onAccept) onAccept();
      navigate(`/tickets/${id}`);
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const handleSelectTicket = (id) => {
    navigate(`/tickets/${id}`);
  };

  const isPending = ticket.status === "pending";
  const isSelected = ticketId && +ticketId === ticket.id;

  const timeLabel = ticket.lastMessage
    ? (() => {
        const d = parseISO(ticket.updatedAt);
        const diff = differenceInCalendarDays(new Date(), d);
        if (diff === 0) return format(d, "HH:mm");
        if (diff === 1) return "Yesterday";
        if (diff <= 7) return format(d, "EEEE");
        return format(d, "dd/MM/yy");
      })()
    : null;

  return (
    <React.Fragment key={ticket.id}>
      <ListItem dense disablePadding className={classes.listItem}>
        <ListItemButton
          onClick={() => {
            if (isPending) return;
            handleSelectTicket(ticket.id);
          }}
          selected={isSelected}
          className={clsx(classes.button, { [classes.pendingRow]: isPending })}
          sx={{ borderLeftColor: ticket.queues?.[0]?.color || "transparent" }}
        >
          {/* Avatar */}
          <Avatar
            src={ticket?.contact?.profilePicUrl}
            alt={ticket?.contact?.name}
            className={classes.avatar}
          />

          {/* Content */}
          <Box className={classes.content}>
            {/* Row 1: name + tags + time */}
            <Box className={classes.topRow}>
              <Typography
                noWrap
                component="span"
                className={classes.contactName}
              >
                {ticket?.contact?.name}
              </Typography>

              {ticket.whatsappId && ticket.whatsapp?.name && (
                <span
                  className={classes.connTag}
                  title={i18n.t("ticketsList.connectionTitle")}
                >
                  {ticket.whatsapp.name}
                </span>
              )}

              {ticket.status === "closed" && (
                <span className={classes.closedPill}>closed</span>
              )}

              {timeLabel && (
                <span className={classes.time}>{timeLabel}</span>
              )}
            </Box>

            {/* Row 2: message preview + action */}
            <Box className={classes.bottomRow}>
              <Box className={classes.preview}>
                {ticket.lastMessage ? (
                  <MarkdownWrapper>{ticket.lastMessage}</MarkdownWrapper>
                ) : (
                  <>&nbsp;</>
                )}
              </Box>

              {isPending ? (
                <ButtonWithSpinner
                  variant="contained"
                  color="primary"
                  size="small"
                  loading={loading}
                  className={classes.acceptBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAcepptTicket(ticket.id);
                  }}
                >
                  {i18n.t("ticketsList.buttons.accept")}
                </ButtonWithSpinner>
              ) : (
                ticket.unreadMessages > 0 && (
                  <Box className={classes.unreadPill}>
                    {ticket.unreadMessages > 99 ? "99+" : ticket.unreadMessages}
                  </Box>
                )
              )}
            </Box>

            {/* Row 3: labels */}
            {ticket.queues?.length > 0 && (
              <Box className={classes.labelsRow}>
                {ticket.queues.map((q) => (
                  <span key={q.id} className={classes.labelChip}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: q.color, flexShrink: 0 }} />
                    {q.name}
                  </span>
                ))}
              </Box>
            )}
          </Box>
        </ListItemButton>
      </ListItem>
      <Divider component="li" />
    </React.Fragment>
  );
};

export default TicketListItem;
